import {debounceTime, filter, map, merge, Observable, of} from 'rxjs';
import {useMemo} from 'react';

import {
  ANIMATION_DURATION,
  useSyncConversationObservable,
} from '@stripe-internal/conversations-ui';
import type {Conversation} from '@stripe-internal/conversations-client';

import {SupportConversationsClient} from '../lib/SupportConversationsClient';

function buildBusyObservable(
  conversation: Conversation | null,
): Observable<boolean> {
  const client = conversation?.client;

  if (!(client instanceof SupportConversationsClient) || !conversation) {
    return of(false);
  }

  const observable = client.busyObservable();
  const busy = observable.pipe(filter((isBusy) => isBusy));
  const idle = observable.pipe(
    filter((isBusy) => !isBusy),
    // Delay idle events to give time for effects to render and animate.
    debounceTime(ANIMATION_DURATION + 100),
  );

  // Re-check isBusy() when the context changes so transitions between clients
  // (for example, assistant to chat) publish the current source of truth.
  return merge(busy, idle, conversation.contextObservable()).pipe(
    map(() => client.isBusy()),
  );
}

export function useConversationClientPending(
  conversation: Conversation | null,
) {
  // Keep the adapter stable for a conversation so ordinary React renders do
  // not tear down the subscription or bypass the delayed idle signal.
  const observableAdapter = useMemo(() => {
    const busyObservable = buildBusyObservable(conversation);
    return {
      observable: () => busyObservable,
      get: () => {
        const client = conversation?.client;
        return client instanceof SupportConversationsClient
          ? client.isBusy()
          : false;
      },
    };
  }, [conversation]);

  return useSyncConversationObservable(observableAdapter);
}
